import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Arrow, TOTAL_COINS } from "./calculator/primitives";
import Stage1Landlord from "./calculator/Stage1Landlord";
import Stage2Amc from "./calculator/Stage2Amc";
import Stage3Investor from "./calculator/Stage3Investor";
import Stage4Tenant from "./calculator/Stage4Tenant";

/* ─── URL hash helpers ─── */
function readInitialFromHash() {
  if (typeof window === "undefined") return null;
  const m = window.location.hash.match(/#calculator\?([^#]+)/);
  if (!m) return null;
  const p = new URLSearchParams(m[1]);
  return {
    net: Number(p.get("net")),
    yr: Number(p.get("yr")),
    disc: Number(p.get("disc")),
    mfee: Number(p.get("mfee")),
    sfee: Number(p.get("sfee")),
    coins: Number(p.get("coins")),
    cur: p.get("cur"),
  };
}

function buildHashParams(state) {
  return new URLSearchParams({
    net: state.annualNet,
    yr: state.yearsOffered,
    disc: state.investorDiscount,
    mfee: state.amcMonthlyPct,
    sfee: state.amcSetupPct,
    coins: state.myCoins,
    cur: state.currency,
  }).toString();
}

/* ─── Pure math derivation ─── */
function useScenarioMath({
  annualNet, yearsOffered, investorDiscount, amcMonthlyPct, amcSetupPct, myCoins,
}) {
  return useMemo(() => {
    const months = yearsOffered * 12;
    const monthlyNet = annualNet / 12;
    const totalFutureNet = annualNet * yearsOffered;
    const targetRaise = totalFutureNet / (1 + investorDiscount / 100);
    const totalInvestorDiscount = totalFutureNet - targetRaise;
    const landlordNetToday = totalFutureNet - totalInvestorDiscount;
    const amcMonthlyFee = monthlyNet * (amcMonthlyPct / 100);
    const netToInvestors = monthlyNet - amcMonthlyFee;
    const pricePerCoin = targetRaise / TOTAL_COINS;
    const amcSetupFee = targetRaise * (amcSetupPct / 100);
    const landlordAdvance = targetRaise - amcSetupFee;
    const perCoinMonthly = netToInvestors / TOTAL_COINS;

    const coins = Math.max(0, Math.min(myCoins, TOTAL_COINS));
    const invInvest = coins * pricePerCoin;
    const invMonthly = coins * perCoinMonthly;
    const invTotal = invMonthly * months;
    const invNet = invTotal - invInvest;
    const invROI = invInvest > 0 ? invNet / invInvest : 0;
    const invAPY = yearsOffered > 0 ? invROI / yearsOffered : 0;
    const invBE = invMonthly > 0 ? Math.min(months, Math.ceil(invInvest / invMonthly)) : 0;

    const splitAmcPct = monthlyNet > 0 ? amcMonthlyFee / monthlyNet : 0;
    const splitInvPct = Math.max(0, 1 - splitAmcPct);

    return {
      months, monthlyNet, totalFutureNet, totalInvestorDiscount, landlordNetToday,
      amcMonthlyFee, netToInvestors, targetRaise, pricePerCoin,
      amcSetupFee, landlordAdvance, perCoinMonthly,
      coins, invInvest, invMonthly, invTotal, invNet, invROI, invAPY, invBE,
      splitAmcPct, splitInvPct,
    };
  }, [annualNet, yearsOffered, investorDiscount, amcMonthlyPct, amcSetupPct, myCoins]);
}

/* ─── Component ─── */
export default function WhatIfCalculator() {
  const { t } = useTranslation();
  const { fmt, meta: cmeta, setCurrency, currency } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fmtM = (usd, d = 1) => fmt(usd, { maxDecimals: d });

  const initial = useMemo(readInitialFromHash, []);

  // Stage 01
  const [annualNet, setAnnualNet] = useState(initial?.net || 60000);
  const [yearsOffered, setYearsOffered] = useState(initial?.yr || 10);
  const [investorDiscount, setInvestorDiscount] = useState(initial?.disc || 20);
  // Stage 02
  const [amcMonthlyPct, setAmcMonthlyPct] = useState(initial?.mfee || 5);
  const [amcSetupPct, setAmcSetupPct] = useState(initial?.sfee || 2);
  // Stage 03
  const [myCoins, setMyCoins] = useState(initial?.coins || 10000);

  // Apply currency from URL once
  useEffect(() => {
    if (initial?.cur && initial.cur !== currency) setCurrency(initial.cur);
  }, []);

  // Sync state → URL hash
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = buildHashParams({
      annualNet, yearsOffered, investorDiscount, amcMonthlyPct, amcSetupPct, myCoins, currency,
    });
    const newHash = `#calculator?${params}`;
    if (window.location.hash.startsWith("#calculator") && window.location.hash !== newHash) {
      window.history.replaceState(null, "", newHash);
    }
  }, [annualNet, yearsOffered, investorDiscount, amcMonthlyPct, amcSetupPct, myCoins, currency]);

  const calc = useScenarioMath({
    annualNet, yearsOffered, investorDiscount, amcMonthlyPct, amcSetupPct, myCoins,
  });

  /* ─── Actions ─── */
  const persistScenario = () => {
    sessionStorage.setItem("lm_scenario", JSON.stringify({
      annualNet, yearsOffered, investorDiscount, amcMonthlyPct, amcSetupPct, myCoins, currency,
    }));
  };

  const copyShareLink = async () => {
    if (typeof window === "undefined") return;
    const params = buildHashParams({
      annualNet, yearsOffered, investorDiscount, amcMonthlyPct, amcSetupPct, myCoins, currency,
    });
    const url = `${window.location.origin}/#calculator?${params}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("share_copied"));
    } catch {
      toast.success(url);
    }
  };

  const downloadShareCard = () => {
    const params = buildHashParams({
      annualNet, yearsOffered, investorDiscount, amcMonthlyPct, amcSetupPct, myCoins, currency,
    });
    const api = process.env.REACT_APP_BACKEND_URL;
    const link = document.createElement("a");
    link.href = `${api}/api/share-card?${params}`;
    link.download = "ejarmint-scenario.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const applyScenario = () => {
    persistScenario();
    if (!user) { navigate("/register"); return; }
    if (user.role === "landlord") navigate("/landlord?prefill=1");
    else if (user.role === "amc") navigate("/amc?prefill=1");
    else navigate("/marketplace");
  };

  return (
    <section id="calculator" className="border-b border-black/10 bg-zinc-50">
      <div className="max-w-[1400px] mx-auto px-6 py-20">
        <div className="font-mono-label text-[11px] text-[#002FA7] mb-4">⎯⎯ {t("calc_tag")}</div>
        <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight max-w-4xl">{t("jc_title")}</h2>
        <p className="mt-4 text-zinc-600 max-w-2xl">{t("jc_sub")}</p>

        <div className="mt-12">
          <Stage1Landlord
            annualNet={annualNet} setAnnualNet={setAnnualNet}
            yearsOffered={yearsOffered} setYearsOffered={setYearsOffered}
            investorDiscount={investorDiscount} setInvestorDiscount={setInvestorDiscount}
            calc={calc} fmtM={fmtM} currencyCode={cmeta.code}
          />
          <Arrow />
          <Stage2Amc
            amcMonthlyPct={amcMonthlyPct} setAmcMonthlyPct={setAmcMonthlyPct}
            amcSetupPct={amcSetupPct} setAmcSetupPct={setAmcSetupPct}
            calc={calc} fmtM={fmtM}
          />
          <Arrow />
          <Stage3Investor
            myCoins={myCoins} setMyCoins={setMyCoins}
            calc={calc} fmtM={fmtM}
          />
          <Arrow />
          <Stage4Tenant calc={calc} fmtM={fmtM} />
        </div>

        {/* SHARE + APPLY CTAs */}
        <div className="mt-10 flex flex-wrap gap-3 justify-end items-center">
          <Button data-testid="calc-download" onClick={downloadShareCard} variant="outline"
            className="rounded-none border-black h-11 px-5 font-mono-label text-[11px]">
            ↧ {t("share_card")}
          </Button>
          <Button data-testid="calc-share" onClick={copyShareLink} variant="outline"
            className="rounded-none border-black h-11 px-5 font-mono-label text-[11px]">
            ⎘ {t("share_link")}
          </Button>
          <Button data-testid="calc-apply" onClick={applyScenario}
            className="rounded-none bg-[#002FA7] hover:bg-[#001F70] h-11 px-5 font-mono-label text-[11px]">
            {!user ? t("apply_register") : user.role === "amc" ? t("apply_amc") : t("apply_landlord")}
          </Button>
        </div>
      </div>
    </section>
  );
}
